import { useTranslation } from 'react-i18next';
import type { EmailLogEntryDetail } from '@/api/emailLog/emailLogApi.types';
import { Icon } from '@/components/common/Icon';
import { EMAIL_PREVIEW_FRAME_HEIGHT_PIXELS } from '@/constants/emailLog';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { EmailLogStatusBadge } from './EmailLogStatusBadge';

interface EmailLogEntryDetailsProps {
  entry: EmailLogEntryDetail;
}

function formatSender(entry: EmailLogEntryDetail): string {
  if (entry.senderName.length === 0) {
    return entry.senderAddress;
  }
  return entry.senderName + ' <' + entry.senderAddress + '>';
}

export function EmailLogEntryDetails({ entry }: EmailLogEntryDetailsProps) {
  const { t } = useTranslation('emailLog');

  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-1 sm:grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-2 text-[13px]">
        <DetailRow label={t('detail.from')} value={formatSender(entry)} />
        <DetailRow label={t('detail.to')} value={entry.recipientAddress} />
        {entry.replyToAddress === null ? null : (
          <DetailRow label={t('detail.replyTo')} value={entry.replyToAddress} />
        )}
        <DetailRow
          label={t('detail.sentAt')}
          value={formatIsoDateTimeToTimestamp(entry.createdAt)}
        />
        <dt className="text-zoomuted font-semibold">{t('detail.status')}</dt>
        <dd className="flex items-center gap-2 flex-wrap text-zooindigo">
          <EmailLogStatusBadge status={entry.status} />
          {entry.failureReason === null ? null : (
            <span>{t('failureReason.' + entry.failureReason)}</span>
          )}
        </dd>
      </dl>

      {entry.htmlBody === null ? (
        <div className="p-4 bg-zoowarningbg border border-zoowarningtext rounded-xl flex gap-3">
          <Icon name="Lock" className="text-zoowarningtext text-xl flex-shrink-0" />
          <p className="text-zoowarningtext text-sm">{t('detail.credentialNotice')}</p>
        </div>
      ) : (
        <iframe
          title={t('detail.previewTitle')}
          sandbox=""
          srcDoc={entry.htmlBody}
          className="w-full rounded-xl border border-zooborder bg-zoowhite"
          style={{ height: EMAIL_PREVIEW_FRAME_HEIGHT_PIXELS }}
        />
      )}
    </div>
  );
}

interface DetailRowProps {
  label: string;
  value: string;
}

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <>
      <dt className="text-zoomuted font-semibold">{label}</dt>
      <dd className="text-zooindigo break-words">{value}</dd>
    </>
  );
}
